import * as React from "react";
import { Box, Grid, Stack, TextField } from "@mui/material";
import CclErrorDialog from "../../../../components/common/cclErrorDialog";
import FlexGrid from "../../../../layouts/flexGrid";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import {
    useDeleteAccountExecutiveMapperIDMutation,
    useSaveFiscalChangesMutation,
    useSearchAccountExecMutation,
} from "../../../../services/cclTokenedCustomerHierarchyApi";
import { AccExecClientResponse, AccountExecRequest } from "../../../../services/types/clientTypes";
import { useDispatch } from "react-redux";
import { updateClientAccountExecSearchParams } from "../../../../app/slices/clientAccountExecSearchConfigSlice";
import AccountManagerDataGridPro from "./accountManagerDataGridPro";
import ComponentLoader from "../../../../components/common/componentLoader";
import EditAccountManagerDialog from "./editAccountManagerDialog";

export interface AccountSearchListParams {
    OnSelectedAccountExecData: (selectAccountExecData: AccExecClientResponse[] | undefined) => void;
    refreshTrigger?: number;
}

export const AccountSearchList: React.FC<AccountSearchListParams> = (props) => {
    // redux state
    const dispatch = useDispatch();
    // const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
    const [showEditAccountManagerDialog, setShowEditAccountManagerDialog] =
        React.useState<boolean>(false);
    const [showSearchToBroad, setShowSearchToBroad] = React.useState<boolean>(false);
    const [searchParentKey, setSearchParentKey] = React.useState<number>();
    const [searchFiscalKey, setSearchFiscalKey] = React.useState<number>();
    const [searchClientName, setSearchClientName] = React.useState<string>("");
    const [searchAccountOwnerName, setSearchAccountOwnerName] = React.useState<string>("");
    //const [searchWild, setSearchWild] = React.useState<boolean>(true);/*  */
    const [searchAccountExec, { isLoading }] = useSearchAccountExecMutation();
    const searchRequestIdRef = React.useRef(0);
    const [saveFiscalChange] = useSaveFiscalChangesMutation();
    const [deleteAccountExecMapper] = useDeleteAccountExecutiveMapperIDMutation();
    const [accountExecNewData, setAccountExecNewData] = React.useState<AccExecClientResponse[]>();
    // const [searchResultString, setSearchResultString] = React.useState<string>("");
    const [accountManagerEditData, setAccountManagerEditData] =
        React.useState<AccExecClientResponse[]>();
    const [editDialogMode, setEditDialogMode] = React.useState<"select" | "edit">("edit");
    const [searchPayload, setSearchPayload] = React.useState<AccountExecRequest>({
        fiscalkey: 0,
        parentKey: 0,
        account_name: "",
        clientName: "",
        isSearchWild: true,
    });

    const runSearch = (payload: AccountExecRequest) => {
        const requestId = ++searchRequestIdRef.current;
        setAccountExecNewData(undefined);

        searchAccountExec(payload)
            .unwrap()
            .then((result) => {
                if (requestId !== searchRequestIdRef.current) return;
                setAccountExecNewData(result);
                if (result && result.length >= 30000) setShowSearchToBroad(true);
            })
            .catch(() => {});

        dispatch(
            updateClientAccountExecSearchParams({
                AccountOwnerName: payload.account_name ?? "",
                ParentKey: payload.parentKey ?? 0,
                IsSearchWild: payload.isSearchWild,
                ClientName: payload.clientName ?? "",
                FiscalKey: payload.fiscalkey ?? 0,
            })
        );
    };

    React.useEffect(() => {
        if (
            searchPayload.account_name &&
            searchPayload.account_name.length === 0 &&
            searchPayload.parentKey &&
            searchPayload.parentKey <= 0 &&
            searchPayload.clientName &&
            searchPayload.clientName.length === 0 &&
            searchPayload.fiscalkey &&
            searchPayload.fiscalkey <= 0
        )
            return;

        runSearch(searchPayload);
        // eslint-disable-next-line
    }, [searchPayload]);

    const isFirstRefresh = React.useRef(true);
    React.useEffect(() => {
        if (props.refreshTrigger === undefined) return;
        if (isFirstRefresh.current) {
            isFirstRefresh.current = false;
            return;
        }
        runSearch(searchPayload);
        // eslint-disable-next-line
    }, [props.refreshTrigger]);

    const getAccountExecDetails = async () => {
        setSearchPayload({
            account_name: searchAccountOwnerName,
            parentKey: searchParentKey ?? 0,
            fiscalkey: searchFiscalKey ?? 0,
            clientName: searchClientName,
            isSearchWild: true,
        });
    };

    const OnHandleEditSave = (
        mapperId: number,
        fiscalYear: number,
        newClientIndicator: number,
        effectiveStartDate: Date | null | undefined,
        effectiveEndDate: Date | null | undefined
    ) => {
        setShowEditAccountManagerDialog(false);
        const OldAccountExeEdit = accountManagerEditData?.filter((a) => a.mapperID === mapperId)[0];
        const updatedRecord = OldAccountExeEdit && {
            ...OldAccountExeEdit,
            fiscalYear,
            newClientIndicator,
            effectiveStartDate: effectiveStartDate ?? OldAccountExeEdit.effectiveStartDate,
            effectiveEndDate: effectiveEndDate ?? OldAccountExeEdit.effectiveEndDate,
        };

        if (editDialogMode === "select") {
            if (updatedRecord) {
                props.OnSelectedAccountExecData([updatedRecord]);
            }
            return;
        }

        saveFiscalChange({
            mapperId: mapperId,
            fiscal_Year: fiscalYear,
            effectiveStartDate: effectiveStartDate ?? null,
            effectiveEndDate: effectiveEndDate ?? null,
            parentKey: OldAccountExeEdit?.parentKey ?? 0,
            clientInd: newClientIndicator,
            account_owner: OldAccountExeEdit?.accountOwnerName ?? "",
            parent_name: OldAccountExeEdit?.parentName ?? "",
        });
        setAccountExecNewData((prev) =>
            prev?.map((a) => (a.mapperID !== mapperId ? a : updatedRecord ?? a))
        );
    };

    const handleSearchKeyPress = async (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            getAccountExecDetails();
        }
    };

    const OnSelectDataList = (mapperId: number) => {
        const selected = accountExecNewData?.filter((a) => a.mapperID === mapperId);
        //props.OnSelectedAccountExecData(selected);
        setAccountManagerEditData(selected);
        setEditDialogMode("select");
        setShowEditAccountManagerDialog(true);
    };

    const OnAccountEdit = (mapperId: number) => {
        setAccountManagerEditData(accountExecNewData?.filter((a) => a.mapperID === mapperId));
        setEditDialogMode("edit");
        setShowEditAccountManagerDialog(true);
    };

    const OnAccountDelete = (mapperId: number) => {
        deleteAccountExecMapper(mapperId);
        setAccountExecNewData(accountExecNewData?.filter((a) => a.mapperID !== mapperId));
    };
    return (
        <Stack height={1} width={1}>
            {false && <ComponentLoader msg={"Updating"} />}
            <CclErrorDialog
                open={showSearchToBroad}
                title={"Too Many Results"}
                msg={
                    "There are too many results for this search. Consider adjusting your search parameters and searching again."
                }
                onOk={() => setShowSearchToBroad(false)}
            />
            <EditAccountManagerDialog
                open={showEditAccountManagerDialog}
                onOk={OnHandleEditSave}
                onCancel={() => setShowEditAccountManagerDialog(false)}
                AccountCEResponseSelect={accountManagerEditData ?? []}
                dialogMode={editDialogMode}
            />
            <Grid item xs={12} md={6}>
                <Box
                    sx={{
                        m: 2,
                        width: 1,

                    }}
                >
                    <Grid container component="form" sx={{alignItems: "center"}}>
                        <Grid item xs={6} md={2.5} sx={{ p: 1 }}>
                            <TextField
                                value={searchFiscalKey}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Fiscal key"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    const number = e.target.value.replace(/[^0-9]/g, "");
                                    setSearchFiscalKey(number === "" ? undefined : parseInt(number));
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2.5} sx={{ p: 1 }}>
                            <TextField
                                value={searchParentKey}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Parent Key"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    const number = e.target.value.replace(/[^0-9]/g, "");
                                    setSearchParentKey(number === "" ? undefined : parseInt(number));
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2.5} sx={{ p: 1 }}>
                            <TextField
                                value={searchClientName}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Client Name"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    setSearchClientName(e.target.value);
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2.5} sx={{ p: 1 }}>
                            <TextField
                                value={searchAccountOwnerName}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Account Owner Name"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    setSearchAccountOwnerName(e.target.value);
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={1} sx={{ p: 1 }}>
                            <CclButton
                                onClick={() => getAccountExecDetails()}
                                aria-label="Search by CHT"
                                sx={{ width: 1, height: 1, maxHeight: "57px", marginLeft: "10px" }}
                                mode={"primary"}                            >
                                Search
                            </CclButton>
                        </Grid>{" "}
                    </Grid>
                </Box>
            </Grid>
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    m: 2,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <FlexGrid>
                    <AccountManagerDataGridPro
                        accountCELists={accountExecNewData ?? []}
                        isLoading={isLoading}
                        showToolbar={!isLoading}
                        OnSelectDetails={OnSelectDataList}
                        OnAccountExecEdit={OnAccountEdit}
                        OnAccountExecDelete={OnAccountDelete}
                    />
                </FlexGrid>
            </Box>
        </Stack>
    );
};

export default AccountSearchList;
